import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  where,
  type Unsubscribe,
} from "firebase/firestore";
import type { Project } from "@shared/types";
import { projectBasePath } from "@domain/project/paths";
import type {
  FloorplanAnnotation,
  AnnotationComment,
} from "@domain/floorplans/annotations";
import { db } from "../firebase";

/**
 * Per-project floorplan annotations at
 *   <projectBasePath>/floorplanAnnotations/{annotationId}
 * with comments at
 *   <projectBasePath>/floorplanAnnotations/{annotationId}/comments/{commentId}
 *
 * Subcollection over single-doc-array because heavy threads can rewrite a
 * sizeable doc on every keystroke (same reason Budget Attachments use a
 * subcollection).
 */
export const annotationRepository = {
  /** Subscribe to all annotations on a single floorplan, ordered by createdAt. */
  watch(
    project: Project,
    floorplanId: string,
    onChange: (items: FloorplanAnnotation[]) => void
  ): Unsubscribe {
    const col = collection(db, projectBasePath(project), "floorplanAnnotations");
    const q = query(
      col,
      where("floorplanId", "==", floorplanId),
      orderBy("createdAt", "asc")
    );
    return onSnapshot(
      q,
      (snap) => {
        onChange(snap.docs.map((d) => d.data() as FloorplanAnnotation));
      },
      (err) => {
        console.error("[annotationRepository.watch]", err);
        onChange([]);
      }
    );
  },

  /** Add a new annotation. Caller supplies a stable id (e.g. crypto.randomUUID). */
  async add(project: Project, annotation: FloorplanAnnotation): Promise<void> {
    const col = collection(db, projectBasePath(project), "floorplanAnnotations");
    await setDoc(doc(col, annotation.id), annotation);
  },

  async remove(project: Project, annotationId: string): Promise<void> {
    // Delete nested comments first so we don't orphan a subcollection.
    const commentsCol = collection(
      db,
      projectBasePath(project),
      "floorplanAnnotations",
      annotationId,
      "comments"
    );
    const snap = await getDocs(commentsCol);
    await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
    await deleteDoc(
      doc(db, projectBasePath(project), "floorplanAnnotations", annotationId)
    );
  },

  /** Subscribe to the comment thread of one annotation. */
  watchComments(
    project: Project,
    annotationId: string,
    onChange: (comments: AnnotationComment[]) => void
  ): Unsubscribe {
    const col = collection(
      db,
      projectBasePath(project),
      "floorplanAnnotations",
      annotationId,
      "comments"
    );
    const q = query(col, orderBy("createdAt", "asc"));
    return onSnapshot(
      q,
      (snap) => {
        onChange(snap.docs.map((d) => d.data() as AnnotationComment));
      },
      (err) => {
        console.error("[annotationRepository.watchComments]", err);
        onChange([]);
      }
    );
  },

  async addComment(
    project: Project,
    annotationId: string,
    comment: Omit<AnnotationComment, "id">
  ): Promise<AnnotationComment> {
    const col = collection(
      db,
      projectBasePath(project),
      "floorplanAnnotations",
      annotationId,
      "comments"
    );
    const ref = await addDoc(col, comment);
    return { ...comment, id: ref.id };
  },

  async removeComment(
    project: Project,
    annotationId: string,
    commentId: string
  ): Promise<void> {
    await deleteDoc(
      doc(
        db,
        projectBasePath(project),
        "floorplanAnnotations",
        annotationId,
        "comments",
        commentId
      )
    );
  },

  /**
   * Bulk-delete every annotation (and its comment subcollection) for a
   * given floorplan. Used by the cascade-delete path when a saved
   * floorplan is removed.
   */
  async removeAllForFloorplan(
    project: Project,
    floorplanId: string
  ): Promise<void> {
    const col = collection(db, projectBasePath(project), "floorplanAnnotations");
    const q = query(col, where("floorplanId", "==", floorplanId));
    const snap = await getDocs(q);
    await Promise.all(snap.docs.map((d) => annotationRepository.remove(project, d.id)));
  },
};
